import {
    afterNextRender,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Injector,
    input,
    OnInit,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    SiHeaderDropdownComponent,
    SiHeaderDropdownItemComponent,
    SiHeaderDropdownTriggerDirective
} from '@siemens/element-ng/header-dropdown';
import { SiLocaleService } from '@siemens/element-ng/localization';
import { AuthService } from '@tran-monorepo/frontend/auth';
import { SkipLinkComponent, UserAvatarComponent } from '@tran-monorepo/frontend/components';
import { LanguageService } from '@tran-monorepo/frontend/services';
import { NavItem } from '@tran-monorepo/interfaces';
import { filter, tap } from 'rxjs';
import { APP_LANGUAGES } from '../../configs/languages.config';
import { HeaderScrollService } from '../../services/header-scroll.service';
import { LanguageCode } from './header.model';

@Component({
    selector: 'tran-ui-header',
    imports: [
        RouterLink,
        RouterLinkActive,
        SiHeaderDropdownComponent,
        SiHeaderDropdownTriggerDirective,
        SiHeaderDropdownItemComponent,
        SkipLinkComponent,
        UserAvatarComponent,
        TranslatePipe
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, AfterViewInit {
    public readonly primaryNavItems = input<NavItem[]>([]);
    public readonly secondaryNavItems = input<NavItem[]>([]);

    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly headerScrollService = inject(HeaderScrollService);
    private readonly authService = inject(AuthService);
    private readonly el = inject(ElementRef);
    private readonly localeService = inject(SiLocaleService);
    private readonly injector = inject(Injector);
    private readonly languageService = inject(LanguageService);

    protected readonly userDetails = this.authService.userIdTokenClaims();
    protected readonly translateService = inject(TranslateService);
    protected readonly isTransparent = computed(() => this.isHomePage() && this.headerScrollService.heroVisible());
    protected readonly isHomeScrolled = computed(() => this.isHomePage() && !this.headerScrollService.heroVisible());
    protected readonly showNavbarBg = computed(() => !this.isHomePage());
    protected readonly languages = APP_LANGUAGES;
    protected readonly mobileMenuOpen = signal(false);
    protected readonly currentLanguage = this.languageService.currentLanguage;
    protected readonly isHomePage = signal(false);
    protected readonly languageDropdownOpen = signal(false);

    private headerResizeObserver: ResizeObserver | undefined;

    ngOnInit(): void {
        this.trackHomePage();
    }

    ngAfterViewInit(): void {
        this.initHeaderHeightObserver();
        this.focusOnLanguageButton();
    }

    protected onLanguageSelect(code: LanguageCode): void {
        this.localeService.locale = code;
        this.closeMobileMenu();
    }

    protected toggleMobileMenu(): void {
        this.mobileMenuOpen.update(open => !open);
    }

    protected closeMobileMenu(): void {
        this.mobileMenuOpen.set(false);
    }

    protected focusOnElement(event: Event, targetId: string): void {
        event.preventDefault();

        const container = document.getElementById(targetId);
        if (!container) {
            (event.target as HTMLElement).blur();
            return;
        }

        const firstFocusableElement = container.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (firstFocusableElement) {
            firstFocusableElement.focus();
        } else {
            container.setAttribute('tabindex', '-1');
            container.focus();
        }
    }

    protected onLanguageDropdownToggle(isOpen: boolean): void {
        this.languageDropdownOpen.set(isOpen);
    }

    private focusOnLanguageButton(): void {
        afterNextRender(
            () => {
                this.focusOnElement(new Event('click'), 'language-selection-container');
            },
            { injector: this.injector }
        );
    }

    private trackHomePage(): void {
        this.isHomePage.set(this.router.url === '/');
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                tap(event => this.isHomePage.set(event.urlAfterRedirects === '/')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private initHeaderHeightObserver(): void {
        const headerElement = this.el.nativeElement.querySelector('header');
        if (!headerElement) {
            return;
        }

        this.headerResizeObserver = new ResizeObserver(entries => {
            const height = entries[0].borderBoxSize[0]?.blockSize ?? entries[0].contentRect.height;
            document.documentElement.style.setProperty('--app-header-height', `${height}px`);
        });
        this.headerResizeObserver.observe(headerElement, { box: 'border-box' });
        this.destroyRef.onDestroy(() => this.headerResizeObserver?.disconnect());
    }
}
